import { useState, useRef, useEffect, useCallback } from "react";
import {
  LuZap, LuRadioTower, LuBuilding2, LuFuel, LuCode, LuWrench, LuMap, LuCloud,
  LuNetwork, LuCable, LuRuler, LuRocket, LuPlugZap, LuHardHat,
  LuImage, LuClapperboard, LuFrame, LuNewspaper, LuChevronDown, LuArrowRight,
} from "react-icons/lu";
import { pageToPath } from "../seo/routing";
import "./Navbar.css";

// Routing ids are unchanged — App.js still owns page state.
const navConfig = [
  { id: "home", label: "Home" },
  {
    id: "offerings", label: "Offerings",
    dropdown: [
      {
        group: "Services",
        items: [
          { id: "service-software", label: "Software Services", Icon: LuCode },
          { id: "service-implementation", label: "Implementation", Icon: LuWrench },
          { id: "service-geospatial", label: "Geospatial", Icon: LuMap },
          { id: "service-managed", label: "Managed Services", Icon: LuCloud },
        ],
      },
      {
        group: "Products",
        items: [
          { id: "product-ungrid", label: "Ungrid", Icon: LuNetwork },
          { id: "product-conduitpro", label: "ConduitPro", Icon: LuCable },
          { id: "product-electricity", label: "SLD Platform", Icon: LuRuler },
          { id: "product-runr", label: "rUNr", Icon: LuRocket },
          { id: "product-energize", label: "Energize / De-energize", Icon: LuPlugZap },
          { id: "product-fueerza", label: "Fueerza", Icon: LuHardHat },
        ],
      },
    ],
  },
  {
    id: "industries", label: "Industries",
    dropdown: [
      {
        group: "Industries",
        items: [
          { id: "industry-utility", label: "Utility", Icon: LuZap },
          { id: "industry-telecom", label: "Telecom", Icon: LuRadioTower },
          { id: "industry-infrastructure", label: "Infrastructure", Icon: LuBuilding2 },
          { id: "industry-oilgas", label: "Oil & Gas", Icon: LuFuel },
        ],
      },
    ],
  },
  { id: "about", label: "About Us" },
  { id: "blogs", label: "Blogs" },
  {
    id: "gallery", label: "Gallery",
    dropdown: [
      {
        group: "Gallery",
        items: [
          { id: "gallery-clicks", label: "Inovaantage Clicks", Icon: LuImage },
          { id: "gallery-videos", label: "Inovaantage Videos", Icon: LuClapperboard },
          { id: "gallery-posters", label: "Inovaantage Posters", Icon: LuFrame },
          { id: "gallery-press", label: "Press Release", Icon: LuNewspaper },
        ],
      },
    ],
  },
];

export default function Navbar({ activePage, setActivePage, scrolled }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);
  const barRef = useRef(null);
  const closeTimer = useRef(null);

  // Lock page scroll while the mobile panel is open
  useEffect(() => {
    document.documentElement.classList.toggle("pn-lock", mobileOpen);
    return () => document.documentElement.classList.remove("pn-lock");
  }, [mobileOpen]);

  // Close menus on outside click / Escape
  useEffect(() => {
    const onDown = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpenMenu(null); setMobileOpen(false); }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Thin orange scroll-progress line along the top edge
  useEffect(() => {
    const update = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      const p = max > 0 ? Math.min(1, window.scrollY / max) : 0;
      if (barRef.current) barRef.current.style.transform = `scaleX(${p})`;
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [activePage]);

  const navigate = useCallback((id) => {
    setActivePage(id);
    setOpenMenu(null);
    setMobileOpen(false);
  }, [setActivePage]);

  // Real <a href> links (crawlable); plain clicks navigate in-app, modified clicks open normally.
  const linkClick = (id) => (e) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(id);
  };

  const isActive = (item) => {
    if (item.id === activePage) return true;
    if (item.dropdown) return item.dropdown.some((g) => g.items.some((i) => i.id === activePage));
    return false;
  };

  // Hover intent for desktop dropdowns (small delay avoids flicker)
  const hoverOpen = (id) => {
    if (window.matchMedia("(hover: hover) and (min-width: 1025px)").matches) {
      clearTimeout(closeTimer.current);
      setOpenMenu(id);
    }
  };
  const hoverClose = () => {
    if (window.matchMedia("(hover: hover) and (min-width: 1025px)").matches) {
      clearTimeout(closeTimer.current);
      closeTimer.current = setTimeout(() => setOpenMenu(null), 140);
    }
  };

  return (
    <header className={"pn" + (scrolled ? " pn--scrolled" : "")} ref={navRef}>
      <div className="pn__progress" aria-hidden="true"><span ref={barRef} /></div>

      <div className="pn__bar">
        <a className="pn__brand" href="/" onClick={linkClick("home")} aria-label="Inovaantage home">
          <img src="/dark_logo.png" alt="Inovaantage logo" width="160" height="30" />
        </a>

        <nav className={"pn__menu" + (mobileOpen ? " is-open" : "")} aria-label="Primary">
          <ul className="pn__pill">
            {navConfig.map((item) => {
              const open = openMenu === item.id;
              const wide = item.dropdown && item.dropdown.length > 1;
              return (
                <li
                  key={item.id}
                  className={"pn__item" + (open ? " is-open" : "")}
                  onMouseEnter={item.dropdown ? () => hoverOpen(item.id) : undefined}
                  onMouseLeave={item.dropdown ? hoverClose : undefined}
                >
                  {item.dropdown ? (
                    <button
                      type="button"
                      className={"pn__link" + (isActive(item) ? " is-active" : "")}
                      aria-haspopup="true"
                      aria-expanded={open}
                      onClick={() => setOpenMenu(open ? null : item.id)}
                    >
                      {item.label}
                      <LuChevronDown className="pn__chev" aria-hidden="true" />
                    </button>
                  ) : (
                    <a
                      className={"pn__link" + (isActive(item) ? " is-active" : "")}
                      href={pageToPath(item.id)}
                      aria-current={activePage === item.id ? "page" : undefined}
                      onClick={linkClick(item.id)}
                    >
                      {item.label}
                    </a>
                  )}

                  {item.dropdown && (
                    <div className={"pn__drop" + (wide ? " pn__drop--wide" : "")} role="menu">
                      {item.dropdown.map((group) => (
                        <div key={group.group} className="pn__group">
                          <p className="pn__glabel">{group.group}</p>
                          {group.items.map(({ id, label, Icon }) => (
                            <a
                              key={id}
                              role="menuitem"
                              href={pageToPath(id)}
                              className={"pn__sub" + (activePage === id ? " is-active" : "")}
                              onClick={linkClick(id)}
                            >
                              <span className="pn__subicon"><Icon aria-hidden="true" /></span>
                              <span>{label}</span>
                            </a>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* Right-side treatment from the reference: a single orange pill.
              It reuses the existing Contact page rather than adding a new CTA. */}
          <a
            className={"pn__pillcta" + (activePage === "contact" ? " is-active" : "")}
            href="/contact"
            onClick={linkClick("contact")}
          >
            Contact Us <LuArrowRight aria-hidden="true" />
          </a>
        </nav>

        <button
          type="button"
          className={"pn__burger" + (mobileOpen ? " is-open" : "")}
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
        >
          <span /><span /><span />
        </button>
      </div>
    </header>
  );
}
